/**
 * Public Routes
 * 
 * Routes accessible without authentication
 */

export const PUBLIC_ROUTES = {
  /**
   * Landing page
   */
  home: (locale: string) => `/${locale}`,

  /**
   * User registration
   */
  register: (locale: string) => `/${locale}/register`,

  /**
   * User login
   */
  login: (locale: string) => `/${locale}/login`,

  /**
   * Superadmin login (site administrators only; no reset password)
   */
  superadminLogin: (locale: string) => `/${locale}/superadmin/login`,

  /**
   * Email verification page
   */
  emailVerification: (locale: string) => `/${locale}/email-verification`,

  /**
   * Forgot password
   */
  forgotPassword: (locale: string) => `/${locale}/forgot-password`,

  /**
   * Reset password (confirm token + new password)
   */
  resetPassword: (locale: string) => `/${locale}/reset-password`,

  /**
   * Logout
   */
  logout: (locale: string) => `/${locale}/logout`,

  /**
   * Payment page (public, for plan upgrades)
   */
  payment: (locale: string) => `/${locale}/payment`,

  /**
   * Public event page (by slug) - simplified format
   * Format: /@{slug}
   */
  publicEvent: (_locale: string, slug: string) => `/@${slug}`,

  /**
   * RSVP page (by token)
   */
  rsvp: (locale: string, token: string) => `/${locale}/rsvp/${token}`,

  /**
   * Campaign questionnaire page (by UUID)
   */
  campaignQuestionnaire: (locale: string, uuid: string) => `/${locale}/campaign/${uuid}/questionnaire`,

  /**
   * Cookie policy
   */
  cookiesPolicy: (locale: string) => `/${locale}/cookies-policy`,

  /**
   * Terms and conditions (JUB-100 / JUB-226)
   */
  terms: (locale: string) => `/${locale}/terms`,

  /**
   * Privacy policy (JUB-100 / JUB-226)
   */
  privacy: (locale: string) => `/${locale}/privacy`,

  /**
   * Point-of-collection data processing notice (JUB-105)
   */
  dataProcessingNotice: (locale: string) => `/${locale}/data-processing-notice`,

  /**
   * Blog index and articles (JUB-123)
   */
  blog: (locale: string) => `/${locale}/blog`,
  blogArticle: (locale: string, slug: string) => `/${locale}/blog/${slug}`,

  /**
   * Frequently asked questions (JUB-100)
   */
  faq: (locale: string) => `/${locale}/faq`,
} as const;

/**
 * JUB-81: public event pages are locale-neutral (`/@{slug}` — the event's own
 * default language decides the rendering). A locale-prefixed variant such as
 * `/en/@slug` or `/ro/@slug` used to fall through to the `:slug` route with the
 * slug "@slug" and 404. Returns the canonical `/@{slug}` path (query and hash
 * kept) to redirect to, or null when the pathname is not such a variant.
 */
export function localeNeutralPublicEventPath(pathname: string, search = "", hash = ""): string | null {
  const match = pathname.match(/^\/(ro|en)\/(@[^/]+)/);
  if (!match) return null;
  return `/${match[2]}${search}${hash}`;
}

